import { useState, useEffect, useCallback } from 'react';
import {
  Settings, Building2, ShieldCheck, Users, Bell, Lock, FileText,
  CreditCard, Puzzle, ChevronRight, Plus, Trash2, CheckCircle,
  XCircle, User, ToggleLeft, ToggleRight, Globe, AlertTriangle,
  Mail, Eye, UserCheck, Receipt, Wallet, Crown, RefreshCw, Clock,
  Copy, Activity, Sparkles, ExternalLink, Zap, Edit3
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { toast } from 'sonner';
import API from '@/lib/api';
import { useAuth } from '@/context/AuthContext';
import { PaymentSetupButton } from '@/components/CardSetupWidget';

const SECTIONS = [
  { id: 'workspace', label: 'Workspace Setup', icon: Settings },
  { id: 'company', label: 'Company Profile', icon: Building2 },
  { id: 'verification', label: 'Verification', icon: ShieldCheck },
  { id: 'team', label: 'Team & Permissions', icon: Users },
  { id: 'notifications', label: 'Notifications', icon: Bell },
  { id: 'security', label: 'Security', icon: Lock },
  { id: 'proposal', label: 'Proposal Defaults', icon: FileText },
  { id: 'payment', label: 'Payment & Billing', icon: CreditCard },
  { id: 'integrations', label: 'Integrations', icon: Puzzle },
];

const PLATS = ['Instagram', 'TikTok', 'YouTube', 'Twitter/X', 'Facebook', 'LinkedIn'];
const TERRS = ['US', 'UK', 'EU', 'APAC', 'Global'];

export default function SettingsView() {
  const { user, hasPermission } = useAuth();
  const [section, setSection] = useState('workspace');
  const [settings, setSettings] = useState(null);
  const [team, setTeam] = useState([]);
  const [funding, setFunding] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      const res = await API.get('/buyer/settings');
      setSettings(res.data.settings);
      setTeam(res.data.team_members || []);
      setFunding(res.data.funding_methods || []);
    } catch { toast.error('Failed to load settings'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const isTeam = settings?.workspace_mode === 'team';
  const canManageSettings = hasPermission('manage_settings');

  if (loading) return <SettingsLoadingSkeleton />;

  // Filter sections based on permissions
  const visibleSections = SECTIONS.filter(s => {
    if (s.id === 'team') return true; // Team section handles its own gating
    if (s.id === 'workspace' || s.id === 'verification') return true; // Always visible
    if (['company', 'proposal', 'integrations', 'security'].includes(s.id)) {
      return canManageSettings;
    }
    if (s.id === 'notifications') return true;
    if (s.id === 'payment') return hasPermission('view_finance') || hasPermission('manage_payment_methods');
    return true;
  });

  return (
    <div data-testid="settings-view" className="flex gap-6">
      {/* Settings Sidebar */}
      <nav className="w-52 flex-shrink-0">
        <div className="sticky top-0 space-y-0.5">
          {visibleSections.map(s => {
            const locked = s.id === 'team' && !isTeam;
            return (
              <button key={s.id} data-testid={`settings-nav-${s.id}`}
                onClick={() => setSection(s.id)}
                className={`w-full flex items-center gap-2 px-3 py-2 rounded-lg text-xs font-medium transition-all
                  ${section === s.id ? 'bg-blue-50 text-blue-700' : 'text-slate-500 hover:text-slate-700 hover:bg-slate-50'}
                  ${locked ? 'opacity-50' : ''}`}>
                <s.icon className="w-3.5 h-3.5" />
                <span className="flex-1 text-left">{s.label}</span>
                {locked && <Lock className="w-3 h-3 text-slate-300" />}
              </button>
            );
          })}
        </div>
      </nav>

      {/* Settings Content */}
      <div className="flex-1 min-w-0 max-w-3xl">
        {section === 'workspace' && <WorkspaceSection settings={settings} onUpdate={load} />}
        {section === 'company' && <CompanySection settings={settings} onUpdate={load} />}
        {section === 'verification' && <VerificationSection user={user} />}
        {section === 'team' && <TeamSection settings={settings} team={team} onUpdate={load} isTeam={isTeam} />}
        {section === 'notifications' && <NotificationsSection settings={settings} onUpdate={load} isTeam={isTeam} />}
        {section === 'security' && <SecuritySection />}
        {section === 'proposal' && <ProposalDefaultsSection settings={settings} onUpdate={load} />}
        {section === 'payment' && <PaymentSection settings={settings} funding={funding} onUpdate={load} isTeam={isTeam} />}
        {section === 'integrations' && <IntegrationsSection settings={settings} />}
      </div>
    </div>
  );
}

// ── Section Header ──
function SectionHeader({ title, description }) {
  return (
    <div className="mb-5">
      <h2 className="text-lg font-bold text-slate-900">{title}</h2>
      {description && <p className="text-slate-500 text-xs mt-0.5">{description}</p>}
    </div>
  );
}

function Card({ children, className = '' }) {
  return <div className={`bg-white rounded-xl border border-slate-100 p-5 mb-4 ${className}`}>{children}</div>;
}

function FieldRow({ label, desc, children }) {
  return (
    <div className="grid grid-cols-3 gap-4 items-start py-3 border-b border-slate-50 last:border-0">
      <div>
        <p className="text-sm font-medium text-slate-700">{label}</p>
        {desc && <p className="text-[10px] text-slate-400 mt-0.5">{desc}</p>}
      </div>
      <div className="col-span-2">{children}</div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════
// ── 1. WORKSPACE SETUP ──
// ═══════════════════════════════════════════════════════════
function WorkspaceSection({ settings, onUpdate }) {
  const [saving, setSaving] = useState(false);
  const mode = settings?.workspace_mode || 'solo';

  const toggle = async (newMode) => {
    setSaving(true);
    try {
      await API.put('/buyer/settings/workspace-mode', { mode: newMode });
      toast.success(`Switched to ${newMode === 'team' ? 'Team' : 'Solo'} Mode`);
      onUpdate();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
    finally { setSaving(false); }
  };

  return (
    <div data-testid="workspace-section">
      <SectionHeader title="Workspace Setup" description="Configure how your workspace operates" />

      <Card>
        <h3 className="font-semibold text-slate-800 mb-4">Workspace Mode</h3>

        <div className="grid grid-cols-2 gap-3 mb-5">
          {/* Solo */}
          <button data-testid="mode-solo" onClick={() => toggle('solo')} disabled={saving}
            className={`p-4 rounded-xl border-2 text-left transition-all ${mode === 'solo' ? 'border-blue-500 bg-blue-50/50' : 'border-slate-200 hover:border-slate-300'}`}>
            <div className="flex items-center gap-2 mb-2">
              <User className="w-5 h-5 text-blue-600" />
              <span className="font-semibold text-slate-800">Solo</span>
              {mode === 'solo' && <Badge className="bg-blue-100 text-blue-700 text-[9px] ml-auto">Active</Badge>}
            </div>
            <p className="text-xs text-slate-500">Optimized for one-person agencies. Simple payment config, streamlined notifications.</p>
          </button>

          {/* Team */}
          <button data-testid="mode-team" onClick={() => toggle('team')} disabled={saving}
            className={`p-4 rounded-xl border-2 text-left transition-all ${mode === 'team' ? 'border-blue-500 bg-blue-50/50' : 'border-slate-200 hover:border-slate-300'}`}>
            <div className="flex items-center gap-2 mb-2">
              <Users className="w-5 h-5 text-emerald-600" />
              <span className="font-semibold text-slate-800">Team</span>
              {mode === 'team' && <Badge className="bg-emerald-100 text-emerald-700 text-[9px] ml-auto">Active</Badge>}
            </div>
            <p className="text-xs text-slate-500">Multi-user collaboration with role-based access, payment admin controls, and team routing.</p>
          </button>
        </div>

        {mode === 'team' && (
          <div className="p-3 bg-emerald-50 rounded-lg border border-emerald-100 text-xs text-emerald-700">
            <p className="font-medium mb-1.5">Team Mode is enabled. What's available:</p>
            <ul className="space-y-0.5 text-emerald-600">
              <li>Team invites and role-based permissions</li>
              <li>Payment/billing access assigned by role</li>
              <li>Notification routing by role/person</li>
              <li>User-specific audit trail</li>
            </ul>
          </div>
        )}

        {mode === 'solo' && (
          <div className="p-3 bg-slate-50 rounded-lg text-xs text-slate-500">
            <p>Solo Mode keeps things simple. Switch to Team Mode anytime to unlock collaboration features. Your data is preserved when switching.</p>
          </div>
        )}
      </Card>

      {/* Mode comparison */}
      <Card>
        <h3 className="font-semibold text-slate-800 mb-3">Mode Comparison</h3>
        <table className="w-full text-xs">
          <thead>
            <tr className="border-b border-slate-100">
              <th className="text-left py-2 text-slate-400 font-medium">Feature</th>
              <th className="text-center py-2 text-slate-400 font-medium">Solo</th>
              <th className="text-center py-2 text-slate-400 font-medium">Team</th>
            </tr>
          </thead>
          <tbody>
            {[
              ['Campaign management', true, true],
              ['Creator connections', true, true],
              ['Proposals & licensing', true, true],
              ['Payment configuration', true, true],
              ['Team member invites', false, true],
              ['Role-based permissions', false, true],
              ['Payment admin controls', false, true],
              ['Notification routing', false, true],
              ['Per-user audit trail', false, true],
            ].map(([feat, solo, team_]) => (
              <tr key={feat} className="border-b border-slate-50">
                <td className="py-2 text-slate-600">{feat}</td>
                <td className="py-2 text-center">{solo ? <CheckCircle className="w-3.5 h-3.5 text-emerald-500 inline" /> : <span className="text-slate-300">-</span>}</td>
                <td className="py-2 text-center"><CheckCircle className="w-3.5 h-3.5 text-emerald-500 inline" /></td>
              </tr>
            ))}
          </tbody>
        </table>
      </Card>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════
// ── 2. COMPANY PROFILE ──
// ═══════════════════════════════════════════════════════════
function CompanySection({ settings, onUpdate }) {
  const cp = settings?.company_profile || {};
  const [form, setForm] = useState({
    company_name: cp.company_name || '', legal_business_name: cp.legal_business_name || '',
    website: cp.website || '', country: cp.country || '',
    business_registration_number: cp.business_registration_number || '',
    billing_address: cp.billing_address || '', logo_url: cp.logo_url || '',
    primary_contact_name: cp.primary_contact_name || '', primary_contact_email: cp.primary_contact_email || '',
    primary_contact_phone: cp.primary_contact_phone || '', agency_type: cp.agency_type || 'agency',
  });
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);

  const uploadLogo = async (file) => {
    if (!file || !file.type.startsWith('image/')) { toast.error('Please choose an image'); return; }
    if (file.size > 5 * 1024 * 1024) { toast.error('Image must be under 5MB'); return; }
    setUploading(true);
    try {
      const fd = new FormData(); fd.append('file', file);
      const res = await API.post('/uploads/avatar', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      setForm(f => ({ ...f, logo_url: res.data.url }));
      toast.success('Logo uploaded');
    } catch (e) { toast.error(e.response?.data?.detail || 'Upload failed'); }
    finally { setUploading(false); }
  };

  const save = async () => {
    setSaving(true);
    try {
      await API.put('/buyer/settings/company-profile', form);
      toast.success('Company profile saved');
      onUpdate();
    } catch { toast.error('Failed to save'); }
    finally { setSaving(false); }
  };

  const u = (k, v) => setForm(f => ({ ...f, [k]: v }));

  return (
    <div data-testid="company-section">
      <SectionHeader title="Company Profile" description="Your business identity and contact information" />

      <Card>
        {/* Logo / Branding Upload */}
        <div className="mb-5 pb-5 border-b border-slate-100">
          <Label className="text-xs font-medium text-slate-700 mb-2 block">Agency Logo / Branding</Label>
          <div className="flex items-center gap-4">
            <label className="relative w-20 h-20 rounded-xl overflow-hidden bg-slate-100 border-2 border-dashed border-slate-200 hover:border-blue-300 flex items-center justify-center cursor-pointer transition-colors flex-shrink-0"
              data-testid="agency-logo-upload">
              {form.logo_url ? (
                <img src={form.logo_url.startsWith('http') ? form.logo_url : `${process.env.REACT_APP_BACKEND_URL}${form.logo_url}`}
                  alt="Logo" className="w-full h-full object-cover" />
              ) : (
                <div className="text-center">
                  {uploading ? <RefreshCw className="w-5 h-5 text-slate-400 animate-spin" /> : <Building2 className="w-6 h-6 text-slate-300" />}
                </div>
              )}
              <input type="file" accept="image/*" className="hidden" onChange={e => uploadLogo(e.target.files?.[0])} />
            </label>
            <div>
              <p className="text-xs text-slate-600 font-medium">Upload your agency logo</p>
              <p className="text-[10px] text-slate-400 mt-0.5">JPEG, PNG, or WebP. Max 5MB. Visible to creators on proposals.</p>
              {form.logo_url && (
                <button type="button" onClick={() => setForm(f => ({...f, logo_url: ''}))}
                  className="text-[10px] text-red-400 hover:text-red-600 mt-1">Remove logo</button>
              )}
            </div>
          </div>
        </div>

        <FieldRow label="Company Name" desc="Display name for your workspace">
          <Input data-testid="cp-company-name" value={form.company_name} onChange={e => u('company_name', e.target.value)} className="h-9 text-sm border-slate-200" />
        </FieldRow>
        <FieldRow label="Legal Business Name" desc="Registered legal entity name">
          <Input value={form.legal_business_name} onChange={e => u('legal_business_name', e.target.value)} className="h-9 text-sm border-slate-200" />
        </FieldRow>
        <FieldRow label="Company Type">
          <Select value={form.agency_type} onValueChange={v => u('agency_type', v)}>
            <SelectTrigger className="h-9 text-sm border-slate-200"><SelectValue /></SelectTrigger>
            <SelectContent className="bg-white">
              <SelectItem value="agency">Agency</SelectItem>
              <SelectItem value="brand">Brand</SelectItem>
              <SelectItem value="production_company">Production Company</SelectItem>
              <SelectItem value="solo">Solo Operator</SelectItem>
            </SelectContent>
          </Select>
        </FieldRow>
        <FieldRow label="Website">
          <Input value={form.website} onChange={e => u('website', e.target.value)} placeholder="https://..." className="h-9 text-sm border-slate-200" />
        </FieldRow>
        <FieldRow label="Country">
          <Input value={form.country} onChange={e => u('country', e.target.value)} className="h-9 text-sm border-slate-200" />
        </FieldRow>
        <FieldRow label="Registration Number">
          <Input value={form.business_registration_number} onChange={e => u('business_registration_number', e.target.value)} className="h-9 text-sm border-slate-200" />
        </FieldRow>
        <FieldRow label="Billing Address">
          <Textarea value={form.billing_address} onChange={e => u('billing_address', e.target.value)} rows={2} className="text-sm border-slate-200" />
        </FieldRow>
      </Card>

      <Card>
        <h3 className="font-semibold text-slate-800 mb-3">Primary Contact</h3>
        <div className="grid grid-cols-3 gap-3">
          <div>
            <Label className="text-[10px] text-slate-500 mb-1 block">Name</Label>
            <Input value={form.primary_contact_name} onChange={e => u('primary_contact_name', e.target.value)} className="h-9 text-sm border-slate-200" />
          </div>
          <div>
            <Label className="text-[10px] text-slate-500 mb-1 block">Email</Label>
            <Input type="email" value={form.primary_contact_email} onChange={e => u('primary_contact_email', e.target.value)} className="h-9 text-sm border-slate-200" />
          </div>
          <div>
            <Label className="text-[10px] text-slate-500 mb-1 block">Phone</Label>
            <Input value={form.primary_contact_phone} onChange={e => u('primary_contact_phone', e.target.value)} className="h-9 text-sm border-slate-200" />
          </div>
        </div>
      </Card>

      <Button data-testid="save-company-btn" onClick={save} disabled={saving} className="bg-blue-600 hover:bg-blue-700 text-white text-xs h-9 px-6">
        {saving ? 'Saving...' : 'Save Company Profile'}
      </Button>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════
// ── 3. VERIFICATION ──
// ═══════════════════════════════════════════════════════════
function VerificationSection({ user }) {
  return (
    <div data-testid="verification-section">
      <SectionHeader title="Verification & Compliance" description="Business verification status and compliance" />
      <Card>
        <FieldRow label="Verification Status">
          <Badge variant="outline" className={`text-xs ${user?.verification_status === 'verified' ? 'bg-emerald-50 text-emerald-700 border-emerald-200' : 'bg-amber-50 text-amber-700 border-amber-200'}`}>
            {user?.verification_status || 'Unverified'}
          </Badge>
        </FieldRow>
        <FieldRow label="Account Email"><span className="text-sm text-slate-700">{user?.email}</span></FieldRow>
        <FieldRow label="Role"><span className="text-sm text-slate-700 capitalize">{user?.role?.replace('_', ' ')}</span></FieldRow>
      </Card>
      <Card>
        <p className="text-xs text-slate-400">Compliance documents and additional verification will be managed here in a future update. Contact support for manual verification.</p>
      </Card>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════
// ── 4. TEAM & PERMISSIONS ──
// ═══════════════════════════════════════════════════════════
function TeamSection({ settings, team, onUpdate, isTeam }) {
  const { user, hasPermission } = useAuth();
  const [showInvite, setShowInvite] = useState(false);
  const [inviteForm, setInviteForm] = useState({ email: '', name: '', roles: ['account_manager'], personal_message: '' });
  const [saving, setSaving] = useState(false);
  const [activityTab, setActivityTab] = useState('members'); // 'members' | 'activity'
  const [activities, setActivities] = useState([]);
  const [activityLoading, setActivityLoading] = useState(false);
  const [editRolesFor, setEditRolesFor] = useState(null);
  const [editRoles, setEditRoles] = useState([]);
  const canManage = hasPermission('manage_team');

  // "Owner" is reserved for the agency creator — never assignable via invite/edit.
  const ROLES = [
    { value: 'account_manager', label: 'Account Manager', icon: UserCheck, desc: 'Campaigns, proposals, engagements' },
    { value: 'finance', label: 'Finance / Payments', icon: Wallet, desc: 'Payment methods, transactions, billing' },
    { value: 'legal', label: 'Legal / Compliance', icon: ShieldCheck, desc: 'View engagements and licenses' },
    { value: 'read_only', label: 'Read Only', icon: Eye, desc: 'View-only access' },
  ];

  const memberRoles = (m) => (m.roles && m.roles.length ? m.roles : (m.role ? [m.role] : []));
  const roleLabel = (val) => (val === 'owner' ? 'Owner / Admin' : (ROLES.find(r => r.value === val)?.label || val));

  const loadActivity = async () => {
    setActivityLoading(true);
    try {
      const res = await API.get('/buyer/settings/team/activity');
      setActivities(res.data.activities || []);
    } catch { /* ignore */ }
    finally { setActivityLoading(false); }
  };

  if (!isTeam) {
    return (
      <div data-testid="team-section">
        <SectionHeader title="Team & Permissions" description="Manage your team members and their access" />
        <Card className="text-center py-10">
          <Lock className="w-10 h-10 text-slate-200 mx-auto mb-3" />
          <p className="text-slate-500 text-sm font-medium">Team Mode Required</p>
          <p className="text-slate-400 text-xs mt-1 max-w-sm mx-auto">Enable Team Mode in Workspace Setup to invite team members and configure role-based permissions.</p>
        </Card>
      </div>
    );
  }

  const activeMembers = team.filter(m => m.status === 'active');
  const pendingMembers = team.filter(m => m.status === 'invited');
  const otherMembers = team.filter(m => !['active', 'invited'].includes(m.status));

  const invite = async () => {
    if (!inviteForm.email.trim()) { toast.error('Email required'); return; }
    if (!inviteForm.roles?.length) { toast.error('Select at least one role'); return; }
    setSaving(true);
    try {
      const res = await API.post('/buyer/settings/team/invite', inviteForm);
      const token = res.data.invite_token;
      const baseUrl = window.location.origin;
      const inviteLink = `${baseUrl}/invite/accept?token=${token}`;
      toast.success(`Invite sent to ${inviteForm.email}`, { duration: 5000 });
      setShowInvite(false);
      setInviteForm({ email: '', name: '', roles: ['account_manager'], personal_message: '' });
      onUpdate();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
    finally { setSaving(false); }
  };

  const updateRoles = async (memberId, roles) => {
    if (!roles.length) { toast.error('A member must have at least one role'); return; }
    try {
      await API.put(`/buyer/settings/team/${memberId}`, { roles });
      toast.success('Roles updated');
      setEditRolesFor(null);
      onUpdate();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const toggleStatus = async (memberId, current) => {
    const status = current === 'active' ? 'deactivated' : 'active';
    try {
      await API.put(`/buyer/settings/team/${memberId}`, { status });
      toast.success(`Member ${status}`);
      onUpdate();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const remove = async (memberId) => {
    if (!window.confirm('Remove this team member? This action cannot be undone.')) return;
    try { await API.delete(`/buyer/settings/team/${memberId}`); toast.success('Removed'); onUpdate(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const resendInvite = async (memberId) => {
    try {
      const res = await API.post(`/buyer/settings/team/${memberId}/resend`);
      const token = res.data.invite_token;
      const baseUrl = window.location.origin;
      const inviteLink = `${baseUrl}/invite/accept?token=${token}`;
      toast.success(`Invite resent! Link: ${inviteLink}`, { duration: 8000 });
      onUpdate();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const revokeInvite = async (memberId) => {
    try {
      await API.post(`/buyer/settings/team/${memberId}/revoke`);
      toast.success('Invite revoked');
      onUpdate();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const copyInviteLink = (token) => {
    const baseUrl = window.location.origin;
    const link = `${baseUrl}/invite/accept?token=${token}`;
    navigator.clipboard.writeText(link);
    toast.success('Invite link copied');
  };

  const timeAgo = (dateStr) => {
    if (!dateStr) return '';
    const diff = Date.now() - new Date(dateStr).getTime();
    const mins = Math.floor(diff / 60000);
    if (mins < 60) return `${mins}m ago`;
    const hrs = Math.floor(mins / 60);
    if (hrs < 24) return `${hrs}h ago`;
    const days = Math.floor(hrs / 24);
    return `${days}d ago`;
  };

  return (
    <div data-testid="team-section">
      <SectionHeader title="Team & Permissions" description="Manage team members and their access roles" />

      {/* Tab switcher: Members / Activity */}
      <div className="flex gap-1 bg-slate-100 rounded-lg p-0.5 mb-4 w-fit">
        <button onClick={() => setActivityTab('members')}
          className={`px-3 py-1.5 rounded-md text-xs font-medium transition-all
            ${activityTab === 'members' ? 'bg-white text-slate-800 shadow-sm' : 'text-slate-400 hover:text-slate-600'}`}>
          Members ({team.length})
        </button>
        <button onClick={() => { setActivityTab('activity'); loadActivity(); }}
          className={`px-3 py-1.5 rounded-md text-xs font-medium transition-all
            ${activityTab === 'activity' ? 'bg-white text-slate-800 shadow-sm' : 'text-slate-400 hover:text-slate-600'}`}>
          Activity
        </button>
      </div>

      {activityTab === 'members' && (
        <>
          {canManage && (
            <div className="flex justify-end mb-4">
              <Button data-testid="invite-team-btn" onClick={() => setShowInvite(true)} className="bg-blue-600 hover:bg-blue-700 text-white text-xs h-8 px-3">
                <Plus className="w-3 h-3 mr-1" />Invite Member
              </Button>
            </div>
          )}

          {showInvite && (
            <InviteDialog
              open={showInvite}
              onClose={() => setShowInvite(false)}
              roles={ROLES}
              inviteForm={inviteForm}
              setInviteForm={setInviteForm}
              onSubmit={invite}
              saving={saving}
            />
          )}

          {/* Active Members */}
          <Card>
            <h3 className="font-semibold text-slate-800 mb-3">Active Members ({activeMembers.length})</h3>
            {activeMembers.length === 0 ? (
              <p className="text-xs text-slate-400 text-center py-6">No active members yet.</p>
            ) : (
              <div className="space-y-2">
                {activeMembers.map(m => {
                  const isMe = m.user_id === user?.id;
                  return (
                    <div key={m.id} data-testid={`team-member-${m.id}`}
                      className={`flex items-center gap-3 p-3 rounded-lg ${isMe ? 'bg-blue-50/50 border border-blue-100' : 'bg-slate-50'}`}>
                      <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center text-blue-700 text-xs font-bold">
                        {(m.name || m.email)?.[0]?.toUpperCase()}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-slate-700">
                          {m.name || m.email}
                          {isMe && <span className="text-[10px] text-blue-500 ml-1.5">(You)</span>}
                        </p>
                        <p className="text-[10px] text-slate-400">{m.email}</p>
                        {editRolesFor === m.id ? (
                          <div className="mt-2 flex flex-wrap gap-1.5" data-testid={`role-editor-${m.id}`}>
                            {ROLES.map(r => {
                              const on = editRoles.includes(r.value);
                              return (
                                <button key={r.value} type="button"
                                  data-testid={`role-edit-${m.id}-${r.value}`}
                                  onClick={() => setEditRoles(on ? editRoles.filter(x => x !== r.value) : [...editRoles, r.value])}
                                  className={`px-2 py-1 rounded-full text-[10px] font-medium border transition-all ${on ? 'bg-blue-600 text-white border-blue-600' : 'bg-white text-slate-600 border-slate-200 hover:border-slate-300'}`}>
                                  {on && <CheckCircle className="w-2.5 h-2.5 inline mr-1" />}{r.label}
                                </button>
                              );
                            })}
                            <Button size="sm" onClick={() => updateRoles(m.id, editRoles)}
                              data-testid={`role-edit-save-${m.id}`}
                              className="h-6 text-[10px] bg-blue-600 hover:bg-blue-700 text-white">Save</Button>
                            <Button size="sm" variant="ghost" onClick={() => setEditRolesFor(null)} className="h-6 text-[10px] text-slate-400">Cancel</Button>
                          </div>
                        ) : (
                          <div className="mt-1 flex flex-wrap gap-1">
                            {memberRoles(m).map(rv => (
                              <Badge key={rv} variant="outline" data-testid={`member-role-${m.id}-${rv}`}
                                className={`text-[9px] ${rv === 'owner' ? 'text-amber-600 border-amber-200' : 'text-slate-500 border-slate-200'}`}>
                                {roleLabel(rv)}
                              </Badge>
                            ))}
                          </div>
                        )}
                      </div>
                      {canManage && !isMe && m.role !== 'owner' && editRolesFor !== m.id && (
                        <button data-testid={`edit-roles-${m.id}`}
                          onClick={() => { setEditRolesFor(m.id); setEditRoles(memberRoles(m).filter(r => r !== 'owner')); }}
                          className="text-slate-400 hover:text-blue-500 p-1" title="Edit roles">
                          <Edit3 className="w-3.5 h-3.5" />
                        </button>
                      )}
                      <Badge variant="outline" className="text-[9px] text-emerald-600 border-emerald-200">active</Badge>
                      {canManage && !isMe && (
                        <>
                          <button data-testid={`toggle-${m.id}`} onClick={() => toggleStatus(m.id, m.status)} className="text-slate-400 hover:text-amber-500 p-1" title="Deactivate">
                            <ToggleRight className="w-4 h-4" />
                          </button>
                          <button data-testid={`remove-${m.id}`} onClick={() => remove(m.id)} className="text-slate-300 hover:text-red-500 p-1" title="Remove">
                            <Trash2 className="w-3.5 h-3.5" />
                          </button>
                        </>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </Card>

          {/* Pending Invites */}
          {pendingMembers.length > 0 && (
            <Card>
              <h3 className="font-semibold text-slate-800 mb-3">
                <Clock className="w-3.5 h-3.5 inline mr-1 text-amber-500" />
                Pending Invites ({pendingMembers.length})
              </h3>
              <div className="space-y-2">
                {pendingMembers.map(m => (
                  <div key={m.id} data-testid={`pending-member-${m.id}`}
                    className="flex items-center gap-3 p-3 bg-amber-50/50 rounded-lg border border-amber-100">
                    <div className="w-8 h-8 rounded-full bg-amber-100 flex items-center justify-center text-amber-700 text-xs font-bold">
                      <Mail className="w-3.5 h-3.5" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-slate-700">{m.name || m.email}</p>
                      <p className="text-[10px] text-slate-400">
                        {m.email} &middot; Invited {timeAgo(m.created_at)}
                        {m.expires_at && <span className="ml-1 text-amber-500">Expires {timeAgo(m.expires_at)}</span>}
                      </p>
                    </div>
                    <div className="flex flex-wrap gap-1 justify-end shrink-0 max-w-[45%]">
                      {memberRoles(m).map(rv => (
                        <Badge key={rv} variant="outline" className="text-[10px] text-slate-500 border-slate-200">
                          {roleLabel(rv)}
                        </Badge>
                      ))}
                    </div>
                    <Badge variant="outline" className="text-[9px] text-amber-600 border-amber-200">pending</Badge>
                    {canManage && (
                      <>
                        {m.invite_token && (
                          <button onClick={() => copyInviteLink(m.invite_token)} className="text-slate-400 hover:text-blue-500 p-1" title="Copy invite link">
                            <Copy className="w-3.5 h-3.5" />
                          </button>
                        )}
                        <button data-testid={`resend-${m.id}`} onClick={() => resendInvite(m.id)} className="text-slate-400 hover:text-blue-500 p-1" title="Resend">
                          <RefreshCw className="w-3.5 h-3.5" />
                        </button>
                        <button data-testid={`revoke-${m.id}`} onClick={() => revokeInvite(m.id)} className="text-slate-300 hover:text-red-500 p-1" title="Revoke">
                          <XCircle className="w-3.5 h-3.5" />
                        </button>
                      </>
                    )}
                  </div>
                ))}
              </div>
            </Card>
          )}

          {/* Expired/Declined/Revoked */}
          {otherMembers.length > 0 && (
            <Card>
              <h3 className="font-semibold text-slate-800 mb-3 text-xs">Past Invites ({otherMembers.length})</h3>
              <div className="space-y-2">
                {otherMembers.map(m => (
                  <div key={m.id} className="flex items-center gap-3 p-2 bg-slate-50 rounded-lg opacity-60">
                    <div className="w-7 h-7 rounded-full bg-slate-200 flex items-center justify-center text-slate-500 text-xs font-bold">
                      {(m.name || m.email)?.[0]?.toUpperCase()}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-xs text-slate-500">{m.name || m.email} &middot; {m.email}</p>
                    </div>
                    <Badge variant="outline" className="text-[9px] text-slate-400 border-slate-200">{m.status}</Badge>
                    {canManage && m.status !== 'active' && (
                      <>
                        <button onClick={() => resendInvite(m.id)} className="text-slate-400 hover:text-blue-500 p-1" title="Resend">
                          <RefreshCw className="w-3 h-3" />
                        </button>
                        <button onClick={() => remove(m.id)} className="text-slate-300 hover:text-red-500 p-1" title="Remove">
                          <Trash2 className="w-3 h-3" />
                        </button>
                      </>
                    )}
                  </div>
                ))}
              </div>
            </Card>
          )}

          {/* Roles Reference */}
          <Card>
            <h3 className="font-semibold text-slate-800 mb-3">Role Reference</h3>
            <div className="space-y-2">
              {ROLES.map(r => (
                <div key={r.value} className="flex items-start gap-2.5 p-2 rounded-lg hover:bg-slate-50">
                  <r.icon className="w-4 h-4 text-slate-400 mt-0.5" />
                  <div>
                    <p className="text-xs font-medium text-slate-700">{r.label}</p>
                    <p className="text-[10px] text-slate-400">{r.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </Card>
        </>
      )}

      {/* Activity Tab */}
      {activityTab === 'activity' && (
        <Card>
          <h3 className="font-semibold text-slate-800 mb-3">
            <Activity className="w-3.5 h-3.5 inline mr-1 text-blue-500" />
            Recent Team Activity
          </h3>
          {activityLoading ? (
            <p className="text-xs text-slate-400 text-center py-6">Loading...</p>
          ) : activities.length === 0 ? (
            <p className="text-xs text-slate-400 text-center py-6">No team activity yet.</p>
          ) : (
            <div className="space-y-2">
              {activities.slice(0, 20).map((act, i) => (
                <div key={act.id || i} className="flex items-start gap-2.5 p-2 border-b border-slate-50 last:border-0">
                  <div className="w-6 h-6 rounded-full bg-slate-100 flex items-center justify-center text-[10px] font-bold text-slate-500 mt-0.5">
                    {act.actor_name?.[0]?.toUpperCase() || '?'}
                  </div>
                  <div className="flex-1">
                    <p className="text-xs text-slate-700">
                      <span className="font-medium">{act.actor_name || 'System'}</span>
                      {' '}
                      <span className="text-slate-500">
                        {act.action?.replace(/_/g, ' ')}
                      </span>
                      {act.metadata_json?.email && (
                        <span className="text-slate-400"> &middot; {act.metadata_json.email}</span>
                      )}
                    </p>
                    <p className="text-[10px] text-slate-400">{timeAgo(act.created_at)}</p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </Card>
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════
// ── 5. NOTIFICATIONS ──
// ═══════════════════════════════════════════════════════════
function NotificationsSection({ settings, onUpdate, isTeam }) {
  const ns = settings?.notification_settings || {};
  const [form, setForm] = useState({ ...ns });
  const [saving, setSaving] = useState(false);

  const save = async () => {
    setSaving(true);
    try {
      await API.put('/buyer/settings/notifications', form);
      toast.success('Notification preferences saved');
      onUpdate();
    } catch { toast.error('Failed'); }
    finally { setSaving(false); }
  };

  const t = (key) => ({ checked: form[key] ?? true, onCheckedChange: v => setForm(f => ({...f, [key]: v})) });

  return (
    <div data-testid="notifications-section">
      <SectionHeader title="Notifications" description="Configure which alerts you receive" />

      <Card>
        <h3 className="font-semibold text-slate-800 mb-3">Channels</h3>
        <FieldRow label="Email Notifications" desc="Receive alerts via email">
          <Switch data-testid="notif-email" {...t('email_notifications')} />
        </FieldRow>
        <FieldRow label="In-App Notifications" desc="Show alerts inside the platform">
          <Switch {...t('in_app_notifications')} />
        </FieldRow>
      </Card>

      <Card>
        <h3 className="font-semibold text-slate-800 mb-3">Alert Types</h3>
        <FieldRow label="Proposal Alerts" desc="New proposals, status changes"><Switch {...t('proposal_alerts')} /></FieldRow>
        <FieldRow label="Creator Responses" desc="When creators respond to offers"><Switch {...t('creator_response_alerts')} /></FieldRow>
        <FieldRow label="Approval Requests" desc="Content awaiting your approval"><Switch {...t('approval_request_alerts')} /></FieldRow>
        <FieldRow label="Transaction Alerts" desc="Payment and funding events"><Switch {...t('transaction_alerts')} /></FieldRow>
        <FieldRow label="Payout Alerts" desc="Creator payout releases"><Switch {...t('payout_alerts')} /></FieldRow>
        <FieldRow label="License Expiry" desc="Upcoming license expirations"><Switch {...t('license_expiry_alerts')} /></FieldRow>
        <FieldRow label="Invite Acceptance" desc="When creators accept invites"><Switch {...t('invite_acceptance_alerts')} /></FieldRow>
      </Card>

      {isTeam && (
        <Card>
          <h3 className="font-semibold text-slate-800 mb-3">Team Routing</h3>
          <p className="text-xs text-slate-400 mb-3">Route specific alert types to team roles (coming soon).</p>
          <div className="p-3 bg-slate-50 rounded-lg text-xs text-slate-400">
            Team notification routing will allow assigning proposal alerts to Account Managers, payment alerts to Finance, and approval alerts to Legal/Compliance roles.
          </div>
        </Card>
      )}

      <Button data-testid="save-notifications-btn" onClick={save} disabled={saving} className="bg-blue-600 hover:bg-blue-700 text-white text-xs h-9 px-6">
        {saving ? 'Saving...' : 'Save Notifications'}
      </Button>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════
// ── 6. SECURITY ──
// ═══════════════════════════════════════════════════════════
function SecuritySection() {
  const [pw, setPw] = useState({ current: '', new_pw: '', confirm: '' });
  const [saving, setSaving] = useState(false);

  const updatePw = async () => {
    if (pw.new_pw !== pw.confirm) { toast.error('Passwords do not match'); return; }
    if (pw.new_pw.length < 8) { toast.error('Min 8 characters'); return; }
    setSaving(true);
    try {
      await API.put('/buyer/settings/security', { current_password: pw.current, new_password: pw.new_pw });
      toast.success('Password updated');
      setPw({ current: '', new_pw: '', confirm: '' });
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
    finally { setSaving(false); }
  };

  return (
    <div data-testid="security-section">
      <SectionHeader title="Security" description="Password and account security" />

      <Card>
        <h3 className="font-semibold text-slate-800 mb-3">Change Password</h3>
        <div className="space-y-3 max-w-sm">
          <div>
            <Label className="text-[10px] text-slate-500 mb-1 block">Current Password</Label>
            <Input type="password" value={pw.current} onChange={e => setPw(p => ({...p, current: e.target.value}))} className="h-9 text-sm border-slate-200" />
          </div>
          <div>
            <Label className="text-[10px] text-slate-500 mb-1 block">New Password</Label>
            <Input data-testid="new-password" type="password" value={pw.new_pw} onChange={e => setPw(p => ({...p, new_pw: e.target.value}))} className="h-9 text-sm border-slate-200" />
          </div>
          <div>
            <Label className="text-[10px] text-slate-500 mb-1 block">Confirm New Password</Label>
            <Input type="password" value={pw.confirm} onChange={e => setPw(p => ({...p, confirm: e.target.value}))} className="h-9 text-sm border-slate-200" />
          </div>
          <Button data-testid="update-password-btn" onClick={updatePw} disabled={saving} className="bg-blue-600 hover:bg-blue-700 text-white text-xs h-9">
            {saving ? 'Updating...' : 'Update Password'}
          </Button>
        </div>
      </Card>

      <Card>
        <h3 className="font-semibold text-slate-800 mb-2">Two-Factor Authentication</h3>
        <p className="text-xs text-slate-400 mb-3">2FA adds an extra layer of security. Coming soon.</p>
        <Button variant="outline" disabled className="text-xs h-8 border-slate-200 text-slate-400">
          <Lock className="w-3 h-3 mr-1" />Enable 2FA (Coming Soon)
        </Button>
      </Card>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════
// ── 7. PROPOSAL DEFAULTS ──
// ═══════════════════════════════════════════════════════════
function ProposalDefaultsSection({ settings, onUpdate }) {
  const pd = settings?.proposal_defaults || {};
  const [form, setForm] = useState({ ...pd });
  const [saving, setSaving] = useState(false);

  const save = async () => {
    setSaving(true);
    try {
      await API.put('/buyer/settings/proposal-defaults', form);
      toast.success('Proposal defaults saved');
      onUpdate();
    } catch { toast.error('Failed'); }
    finally { setSaving(false); }
  };

  const toggleArr = (key, val) => {
    setForm(f => {
      const arr = f[key] || [];
      return { ...f, [key]: arr.includes(val) ? arr.filter(x => x !== val) : [...arr, val] };
    });
  };

  return (
    <div data-testid="proposal-section">
      <SectionHeader title="Proposal & Agreement Defaults" description="Pre-fill values for new proposals. Editable per engagement." />

      <Card>
        <FieldRow label="Default Governing Law" desc="Required before any proposal can be sent. Used for jurisdiction-specific clauses (e.g. moral rights).">
          <Select value={form.default_governing_law || ''} onValueChange={v => setForm(f => ({...f, default_governing_law: v}))}>
            <SelectTrigger data-testid="default-governing-law" className="h-9 text-sm border-slate-200 max-w-xs"><SelectValue placeholder="Select jurisdiction…" /></SelectTrigger>
            <SelectContent className="bg-white">
              <SelectItem value="US-CA">United States — California</SelectItem>
              <SelectItem value="US-NY">United States — New York</SelectItem>
              <SelectItem value="US-TX">United States — Texas</SelectItem>
              <SelectItem value="US">United States — Other</SelectItem>
              <SelectItem value="UK">United Kingdom</SelectItem>
              <SelectItem value="EU">European Union</SelectItem>
              <SelectItem value="FR">France</SelectItem>
              <SelectItem value="DE">Germany</SelectItem>
              <SelectItem value="CA">Canada</SelectItem>
              <SelectItem value="AU">Australia</SelectItem>
              <SelectItem value="OTHER">Other</SelectItem>
            </SelectContent>
          </Select>
        </FieldRow>
        <FieldRow label="Default Dispute Resolution" desc="Applied to new proposals; overridable per deal.">
          <Select value={form.default_dispute_resolution || 'mediation_arbitration'} onValueChange={v => setForm(f => ({...f, default_dispute_resolution: v}))}>
            <SelectTrigger className="h-9 text-sm border-slate-200 max-w-xs"><SelectValue /></SelectTrigger>
            <SelectContent className="bg-white">
              <SelectItem value="litigation">Litigation</SelectItem>
              <SelectItem value="arbitration">Binding Arbitration</SelectItem>
              <SelectItem value="mediation_arbitration">Mediation, then Arbitration</SelectItem>
              <SelectItem value="mediation_litigation">Mediation, then Litigation</SelectItem>
            </SelectContent>
          </Select>
        </FieldRow>
      </Card>

      <Card>
        <FieldRow label="Default Platforms" desc="Pre-selected platforms for proposals">
          <div className="flex flex-wrap gap-1.5">
            {PLATS.map(p => (
              <button key={p} onClick={() => toggleArr('default_platforms', p)}
                className={`px-2.5 py-1 rounded-md text-xs font-medium border transition-all
                  ${(form.default_platforms || []).includes(p) ? 'bg-blue-600 text-white border-blue-600' : 'bg-white text-slate-500 border-slate-200'}`}>
                {p}
              </button>
            ))}
          </div>
        </FieldRow>
        <FieldRow label="Default Territories" desc="Pre-selected territories">
          <div className="flex flex-wrap gap-1.5">
            {TERRS.map(t => (
              <button key={t} onClick={() => toggleArr('default_territories', t)}
                className={`px-2.5 py-1 rounded-md text-xs font-medium border transition-all
                  ${(form.default_territories || []).includes(t) ? 'bg-blue-600 text-white border-blue-600' : 'bg-white text-slate-500 border-slate-200'}`}>
                {t}
              </button>
            ))}
          </div>
        </FieldRow>
        <FieldRow label="Default Usage Types" desc="Pre-selected usage categories">
          <div className="flex flex-wrap gap-1.5">
            {['ad', 'editorial', 'social', 'broadcast'].map(u => (
              <button key={u} onClick={() => toggleArr('default_usage_types', u)}
                className={`px-2.5 py-1 rounded-md text-xs font-medium border transition-all capitalize
                  ${(form.default_usage_types || []).includes(u) ? 'bg-blue-600 text-white border-blue-600' : 'bg-white text-slate-500 border-slate-200'}`}>
                {u}
              </button>
            ))}
          </div>
        </FieldRow>
      </Card>

      <Card>
        <FieldRow label="Payment Structure" desc="Default funding approach">
          <Select value={form.default_payment_structure || 'full_upfront'} onValueChange={v => setForm(f => ({...f, default_payment_structure: v}))}>
            <SelectTrigger className="h-9 text-sm border-slate-200 max-w-xs"><SelectValue /></SelectTrigger>
            <SelectContent className="bg-white">
              <SelectItem value="full_upfront">Full Upfront</SelectItem>
              <SelectItem value="milestone">Milestone-Based</SelectItem>
              <SelectItem value="on_completion">On Completion</SelectItem>
            </SelectContent>
          </Select>
        </FieldRow>
        <FieldRow label="Approval Before Publication" desc="Require creator approval before content goes live">
          <Switch checked={form.default_approval_before_publication ?? true}
            onCheckedChange={v => setForm(f => ({...f, default_approval_before_publication: v}))} />
        </FieldRow>
        <FieldRow label="Review Window" desc="Days for creator to review content">
          <Input type="number" value={form.default_review_window_days || 5} onChange={e => setForm(f => ({...f, default_review_window_days: parseInt(e.target.value) || 5}))}
            className="h-9 text-sm border-slate-200 max-w-[100px]" />
        </FieldRow>
        <FieldRow label="Revision Rounds" desc="Max revisions before escalation">
          <Input type="number" value={form.default_revision_rounds || 2} onChange={e => setForm(f => ({...f, default_revision_rounds: parseInt(e.target.value) || 2}))}
            className="h-9 text-sm border-slate-200 max-w-[100px]" />
        </FieldRow>
        <FieldRow label="Default Contract Term" desc="Months">
          <Input type="number" value={form.default_contract_term_months || 6} onChange={e => setForm(f => ({...f, default_contract_term_months: parseInt(e.target.value) || 6}))}
            className="h-9 text-sm border-slate-200 max-w-[100px]" />
        </FieldRow>
        <FieldRow label="Custom Clauses" desc="Default additional terms">
          <Textarea value={form.default_custom_clauses || ''} onChange={e => setForm(f => ({...f, default_custom_clauses: e.target.value}))}
            rows={3} className="text-sm border-slate-200" placeholder="Any standard clauses to include in proposals..." />
        </FieldRow>
      </Card>

      <Button data-testid="save-proposal-defaults-btn" onClick={save} disabled={saving} className="bg-blue-600 hover:bg-blue-700 text-white text-xs h-9 px-6">
        {saving ? 'Saving...' : 'Save Proposal Defaults'}
      </Button>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════
// ── 8. PAYMENT & BILLING ──
// ═══════════════════════════════════════════════════════════

function StripeFundingMethods() {
  const [methods, setMethods] = useState([]);
  const [defaultPM, setDefaultPM] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      const res = await API.get('/billing/agency/payment-methods');
      setMethods(res.data.payment_methods || []);
      setDefaultPM(res.data.default_payment_method);
    } catch { /* ignore */ }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const makeDefault = async (pmId) => {
    try {
      await API.post('/billing/agency/payment-methods/default', { payment_method_id: pmId });
      toast.success('Default payment method updated');
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const remove = async (pmId) => {
    if (!window.confirm('Remove this payment method?')) return;
    try {
      await API.delete(`/billing/agency/payment-methods/${pmId}`);
      toast.success('Payment method removed');
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  if (loading) return <Card><div className="h-24 flex items-center justify-center text-slate-400 text-sm">Loading...</div></Card>;

  return (
    <div data-testid="stripe-funding-methods">
      <Card>
        <div className="flex items-center justify-between mb-4">
          <div>
            <h3 className="font-semibold text-slate-800">Payment Methods</h3>
            <p className="text-xs text-slate-400 mt-0.5">Saved via Stripe — used to fund deals with creators.</p>
          </div>
          <PaymentSetupButton buttonLabel="Add method" className="h-8 text-xs" />
        </div>

        {methods.length === 0 ? (
          <div className="text-center py-8">
            <CreditCard className="w-8 h-8 text-slate-200 mx-auto mb-2" />
            <p className="text-slate-400 text-xs">No payment methods saved</p>
            <p className="text-[10px] text-slate-300 mt-0.5">Add a card or bank account via Stripe to fund deals.</p>
          </div>
        ) : (
          <div className="space-y-2">
            {methods.map(m => (
              <div key={m.id} data-testid={`stripe-pm-${m.last4 || m.id}`}
                className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg">
                <CreditCard className="w-4 h-4 text-slate-400 flex-shrink-0" />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-slate-700">{m.label}</p>
                  <p className="text-[10px] text-slate-400 capitalize">{m.type?.replace(/_/g, ' ')}</p>
                </div>
                {m.is_default ? (
                  <Badge className="bg-blue-100 text-blue-700 text-[9px] flex-shrink-0">Default</Badge>
                ) : (
                  <Button variant="ghost" size="sm" onClick={() => makeDefault(m.id)}
                    data-testid={`set-default-pm-${m.last4 || m.id}`}
                    className="text-[10px] text-slate-400 h-6 px-2 flex-shrink-0">Set Default</Button>
                )}
                <button onClick={() => remove(m.id)} data-testid={`remove-pm-${m.last4 || m.id}`}
                  className="text-slate-300 hover:text-red-500 flex-shrink-0"><Trash2 className="w-3.5 h-3.5" /></button>
              </div>
            ))}
          </div>
        )}
      </Card>
      <p className="text-[10px] text-slate-400 mt-2">All payment details are securely stored by Stripe. Mylikeness never sees or stores your full card or bank details.</p>
    </div>
  );
}


function PaymentSection({ settings, funding, onUpdate, isTeam }) {
  const bp = settings?.billing_profile || {};
  const td = settings?.transaction_defaults || {};
  const bc = settings?.billing_contacts || {};
  const ps = settings?.processor_status || {};
  const [billingForm, setBillingForm] = useState({ ...bp });
  const [txForm, setTxForm] = useState({ ...td });
  const [contactForm, setContactForm] = useState({ ...bc });
  const [saving, setSaving] = useState(false);
  const [showAddMethod, setShowAddMethod] = useState(false);
  const [methodForm, setMethodForm] = useState({ method_type: 'card', label: '', is_default: false });
  const [subSection, setSubSection] = useState('subscription');

  const saveBilling = async () => {
    setSaving(true);
    try { await API.put('/buyer/settings/billing-profile', billingForm); toast.success('Billing profile saved'); onUpdate(); }
    catch { toast.error('Failed'); }
    finally { setSaving(false); }
  };

  const saveTxDefaults = async () => {
    setSaving(true);
    try { await API.put('/buyer/settings/transaction-defaults', txForm); toast.success('Transaction defaults saved'); onUpdate(); }
    catch { toast.error('Failed'); }
    finally { setSaving(false); }
  };

  const saveContacts = async () => {
    setSaving(true);
    try { await API.put('/buyer/settings/billing-contacts', contactForm); toast.success('Billing contacts saved'); onUpdate(); }
    catch { toast.error('Failed'); }
    finally { setSaving(false); }
  };

  const addMethod = async () => {
    if (!methodForm.label) { toast.error('Label required'); return; }
    try {
      await API.post('/buyer/settings/funding-methods', methodForm);
      toast.success('Funding method added');
      setShowAddMethod(false);
      setMethodForm({ method_type: 'card', label: '', is_default: false });
      onUpdate();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const setDefault = async (id) => {
    try { await API.put(`/buyer/settings/funding-methods/${id}/default`); toast.success('Default updated'); onUpdate(); }
    catch { toast.error('Failed'); }
  };

  const removeMethod = async (id) => {
    try { await API.delete(`/buyer/settings/funding-methods/${id}`); toast.success('Removed'); onUpdate(); }
    catch { toast.error('Failed'); }
  };

  const payTabs = [
    { id: 'subscription', label: 'Subscription' },
    { id: 'funding', label: 'Payment Methods' },
    { id: 'billing', label: 'Billing Profile' },
    { id: 'processor', label: 'Processor Status' },
  ];
  if (isTeam) payTabs.splice(3, 0, { id: 'admin', label: 'Payment Admin' });

  return (
    <div data-testid="payment-section">
      <SectionHeader title="Payment & Billing" description="On-platform payment configuration" />

      <div className="flex gap-1 bg-slate-100 rounded-lg p-0.5 mb-5 overflow-x-auto">
        {payTabs.map(t => (
          <button key={t.id} onClick={() => setSubSection(t.id)}
            className={`px-3 py-1.5 rounded-md text-xs font-medium transition-all whitespace-nowrap
              ${subSection === t.id ? 'bg-white text-slate-800 shadow-sm' : 'text-slate-400 hover:text-slate-600'}`}>
            {t.label}
          </button>
        ))}
      </div>

      {/* A. Billing Profile */}
      {subSection === 'billing' && (
        <>
          <Card>
            <FieldRow label="Paying Entity" desc="Name that appears on transactions">
              <Input value={billingForm.paying_entity_name || ''} onChange={e => setBillingForm(f => ({...f, paying_entity_name: e.target.value}))} className="h-9 text-sm border-slate-200" />
            </FieldRow>
            <FieldRow label="Legal Billing Name">
              <Input value={billingForm.legal_billing_name || ''} onChange={e => setBillingForm(f => ({...f, legal_billing_name: e.target.value}))} className="h-9 text-sm border-slate-200" />
            </FieldRow>
            <FieldRow label="Billing Address">
              <Textarea value={billingForm.billing_address || ''} onChange={e => setBillingForm(f => ({...f, billing_address: e.target.value}))} rows={2} className="text-sm border-slate-200" />
            </FieldRow>
            <FieldRow label="Tax ID / VAT / GST">
              <Input value={billingForm.tax_id || ''} onChange={e => setBillingForm(f => ({...f, tax_id: e.target.value}))} className="h-9 text-sm border-slate-200" />
            </FieldRow>
            <FieldRow label="Billing Email">
              <Input type="email" value={billingForm.billing_email || ''} onChange={e => setBillingForm(f => ({...f, billing_email: e.target.value}))} className="h-9 text-sm border-slate-200" />
            </FieldRow>
            <FieldRow label="Default Currency">
              <Select value={billingForm.default_currency || 'USD'} onValueChange={v => setBillingForm(f => ({...f, default_currency: v}))}>
                <SelectTrigger className="h-9 text-sm border-slate-200 max-w-[120px]"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-white">
                  <SelectItem value="USD">USD</SelectItem>
                  <SelectItem value="EUR">EUR</SelectItem>
                  <SelectItem value="GBP">GBP</SelectItem>
                </SelectContent>
              </Select>
            </FieldRow>
          </Card>
          <Card className="mt-3">
            <h4 className="text-xs font-semibold text-slate-700 mb-3 uppercase tracking-wider">Billing Contacts</h4>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-xs text-slate-500">Billing contact name</Label>
                <Input placeholder="Name" value={contactForm.billing_contact_name || ''}
                  onChange={e => setContactForm(f => ({...f, billing_contact_name: e.target.value}))} className="h-9 text-sm border-slate-200" />
                <Label className="text-xs text-slate-500">Billing contact email</Label>
                <Input placeholder="Email" type="email" value={contactForm.billing_contact_email || ''}
                  onChange={e => setContactForm(f => ({...f, billing_contact_email: e.target.value}))} className="h-9 text-sm border-slate-200" />
              </div>
              <div className="space-y-2">
                <Label className="text-xs text-slate-500">Finance contact name</Label>
                <Input placeholder="Name" value={contactForm.finance_contact_name || ''}
                  onChange={e => setContactForm(f => ({...f, finance_contact_name: e.target.value}))} className="h-9 text-sm border-slate-200" />
                <Label className="text-xs text-slate-500">Finance contact email</Label>
                <Input placeholder="Email" type="email" value={contactForm.finance_contact_email || ''}
                  onChange={e => setContactForm(f => ({...f, finance_contact_email: e.target.value}))} className="h-9 text-sm border-slate-200" />
              </div>
            </div>
          </Card>
          <div className="flex gap-3 mt-3">
            <Button data-testid="save-billing-btn" onClick={() => { saveBilling(); saveContacts(); }} disabled={saving} className="bg-blue-600 hover:bg-blue-700 text-white text-xs h-9 px-6">
              {saving ? 'Saving...' : 'Save Billing Profile'}
            </Button>
          </div>
        </>
      )}

      {/* B. Funding Methods — Real Stripe Payment Methods */}
      {subSection === 'funding' && <StripeFundingMethods />}

      {/* C. Processor Status */}
      {subSection === 'processor' && (
        <Card>
          <h3 className="font-semibold text-slate-800 mb-4">Payment Processor Status</h3>
          <div className="space-y-3">
            {[
              { label: 'Payment Processor', value: ps.provider === 'none' ? 'Not Configured' : ps.provider, ok: ps.provider !== 'none' },
              { label: 'Processor Enabled', value: ps.enabled ? 'Yes' : 'No', ok: ps.enabled },
              { label: 'Mode', value: ps.mode === 'live' ? 'Live' : 'Test', ok: ps.mode === 'live' },
              { label: 'Billing Profile', value: ps.billing_profile_ready ? 'Ready' : 'Incomplete', ok: ps.billing_profile_ready },
              { label: 'Funding Method', value: ps.funding_method_ready ? 'Ready' : 'No method added', ok: ps.funding_method_ready },
            ].map(s => (
              <div key={s.label} className="flex items-center justify-between py-2 border-b border-slate-50 last:border-0">
                <span className="text-sm text-slate-600">{s.label}</span>
                <div className="flex items-center gap-1.5">
                  <span className={`text-xs font-medium ${s.ok ? 'text-emerald-600' : 'text-amber-600'}`}>{s.value}</span>
                  {s.ok ? <CheckCircle className="w-3.5 h-3.5 text-emerald-500" /> : <AlertTriangle className="w-3.5 h-3.5 text-amber-400" />}
                </div>
              </div>
            ))}
          </div>
          <div className="mt-4 p-3 bg-slate-50 rounded-lg">
            <p className="text-xs text-slate-500">This platform supports Stripe Connect and Adyen for Platforms-style payment flows. Payment processor integration will be configured when you're ready for live transactions.</p>
          </div>
        </Card>
      )}

      {/* D. Payment Admin (Team only) */}
      {subSection === 'admin' && isTeam && (
        <Card>
          <h3 className="font-semibold text-slate-800 mb-3">Payment Admin Controls</h3>
          <p className="text-xs text-slate-400 mb-4">Assign payment responsibilities to team roles.</p>
          {[
            { label: 'Manage Funding Methods', desc: 'Add/remove payment methods' },
            { label: 'Initiate Transactions', desc: 'Fund deals on-platform' },
            { label: 'Approve Payments', desc: 'Approve payment actions' },
            { label: 'View Transaction History', desc: 'See all transactions' },
            { label: 'View Billing Statements', desc: 'Access billing and fees' },
            { label: 'Manage Refunds', desc: 'Handle refund/dispute actions' },
          ].map(p => (
            <div key={p.label} className="flex items-center justify-between py-2.5 border-b border-slate-50 last:border-0">
              <div>
                <p className="text-sm text-slate-700">{p.label}</p>
                <p className="text-[10px] text-slate-400">{p.desc}</p>
              </div>
              <Badge variant="outline" className="text-[10px] border-slate-200 text-slate-500">Owner, Finance</Badge>
            </div>
          ))}
        </Card>
      )}

      {/* E. Subscription (moved from standalone tab) */}
      {subSection === 'subscription' && <SubscriptionSection />}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════
// ── 9. INTEGRATIONS ──
// ═══════════════════════════════════════════════════════════
function IntegrationsSection({ settings }) {
  const integrations = [
    { name: 'Stripe Connect', desc: 'Process on-platform payments and creator payouts', status: 'not_configured', icon: CreditCard },
    { name: 'Adyen for Platforms', desc: 'Alternative payment processing for marketplace flows', status: 'not_configured', icon: Wallet },
    { name: 'E-Sign Provider', desc: 'Digital contract and agreement signing', status: 'planned', icon: FileText },
    { name: 'Slack', desc: 'Team notifications and alerts', status: 'planned', icon: Bell },
    { name: 'Accounting Software', desc: 'Sync billing and transaction data', status: 'planned', icon: Receipt },
  ];

  return (
    <div data-testid="integrations-section">
      <SectionHeader title="Integrations" description="Connect external services and tools" />
      <div className="space-y-3">
        {integrations.map(int => (
          <Card key={int.name} className="!mb-0">
            <div className="flex items-center gap-4">
              <div className="w-10 h-10 rounded-lg bg-slate-50 flex items-center justify-center">
                <int.icon className="w-5 h-5 text-slate-400" />
              </div>
              <div className="flex-1">
                <p className="text-sm font-medium text-slate-700">{int.name}</p>
                <p className="text-[10px] text-slate-400">{int.desc}</p>
              </div>
              <Badge variant="outline" className={`text-[10px] ${int.status === 'not_configured' ? 'text-amber-600 border-amber-200' : 'text-slate-400 border-slate-200'}`}>
                {int.status === 'not_configured' ? 'Not Configured' : 'Planned'}
              </Badge>
              <Button variant="outline" size="sm" disabled={int.status === 'planned'}
                className="text-xs h-7 border-slate-200">
                {int.status === 'planned' ? 'Coming Soon' : 'Configure'}
              </Button>
            </div>
          </Card>
        ))}
      </div>
    </div>
  );
}

// ── Loading Skeleton ──
function SettingsLoadingSkeleton() {
  return (
    <div className="flex gap-6">
      <div className="w-52 space-y-2 animate-pulse">
        {[1,2,3,4,5,6,7,8,9].map(i => <div key={i} className="h-8 bg-slate-100 rounded-lg" />)}
      </div>
      <div className="flex-1 space-y-4 animate-pulse">
        <div className="h-8 bg-slate-100 rounded-lg w-48" />
        <div className="h-48 bg-slate-100 rounded-xl" />
        <div className="h-32 bg-slate-100 rounded-xl" />
      </div>
    </div>
  );
}


// ═══════════════════════════════════════════════════════════
// ── Invite Dialog — role cards + personal message ──
// ═══════════════════════════════════════════════════════════
function InviteDialog({ open, onClose, roles, inviteForm, setInviteForm, onSubmit, saving }) {
  const validEmail = /\S+@\S+\.\S+/.test(inviteForm.email);
  const selectedRoles = inviteForm.roles || [];
  const canSend = validEmail && selectedRoles.length > 0 && !saving;
  const toggleRole = (val) => setInviteForm(f => {
    const cur = f.roles || [];
    return { ...f, roles: cur.includes(val) ? cur.filter(x => x !== val) : [...cur, val] };
  });

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-lg p-0 overflow-hidden" data-testid="invite-dialog">
        <div className="p-6 pb-4 border-b border-slate-100">
          <DialogHeader>
            <DialogTitle className="text-lg font-semibold text-slate-900">Invite team member</DialogTitle>
            <DialogDescription className="text-xs text-slate-500">
              They'll get an email with a link to join your workspace. The email they receive will be pre-filled — they just need to set a password.
            </DialogDescription>
          </DialogHeader>
        </div>

        <div className="px-6 py-5 space-y-5 max-h-[70vh] overflow-y-auto">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-[11px] text-slate-600 font-medium">Email <span className="text-red-500">*</span></Label>
              <Input
                data-testid="invite-dialog-email" type="email"
                placeholder="sam@acme.com" value={inviteForm.email}
                onChange={e => setInviteForm(f => ({ ...f, email: e.target.value }))}
                className="h-10 mt-1 text-sm"
              />
            </div>
            <div>
              <Label className="text-[11px] text-slate-600 font-medium">Name <span className="text-slate-400">(optional)</span></Label>
              <Input
                data-testid="invite-dialog-name"
                placeholder="Sam Finance" value={inviteForm.name}
                onChange={e => setInviteForm(f => ({ ...f, name: e.target.value }))}
                className="h-10 mt-1 text-sm"
              />
            </div>
          </div>

          <div>
            <Label className="text-[11px] text-slate-600 font-medium mb-2 block">Choose their role(s) <span className="text-red-500">*</span> <span className="text-slate-400 font-normal">— select one or more; permissions combine</span></Label>
            <div className="grid grid-cols-1 gap-2">
              {roles.map(r => {
                const selected = selectedRoles.includes(r.value);
                const Icon = r.icon;
                return (
                  <button
                    key={r.value}
                    type="button"
                    onClick={() => toggleRole(r.value)}
                    data-testid={`invite-role-${r.value}`}
                    aria-pressed={selected}
                    className={`text-left p-3 rounded-xl border-2 transition-all flex items-start gap-3 ${
                      selected ? 'border-blue-500 bg-blue-50/40' : 'border-slate-200 hover:border-slate-300 bg-white'
                    }`}
                  >
                    <div className={`w-8 h-8 rounded-lg flex items-center justify-center shrink-0 ${
                      selected ? 'bg-blue-600 text-white' : 'bg-slate-100 text-slate-600'
                    }`}>
                      <Icon className="w-4 h-4" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-semibold text-slate-900">{r.label}</p>
                      <p className="text-[11px] text-slate-500 leading-tight mt-0.5">{r.desc}</p>
                    </div>
                    {selected && <CheckCircle className="w-4 h-4 text-blue-600 shrink-0 mt-0.5" />}
                  </button>
                );
              })}
            </div>
          </div>

          <div>
            <Label className="text-[11px] text-slate-600 font-medium">Personal message <span className="text-slate-400">(optional — included in the invite email)</span></Label>
            <Textarea
              data-testid="invite-dialog-message"
              placeholder={`"Hey Sam, joining us for our finance ops — can't wait to work together!"`}
              value={inviteForm.personal_message}
              onChange={e => setInviteForm(f => ({ ...f, personal_message: e.target.value.slice(0, 500) }))}
              rows={3}
              className="mt-1 text-sm resize-none"
            />
            <div className="flex justify-between items-center mt-1">
              <p className="text-[10px] text-slate-400">Max 500 characters.</p>
              <p className="text-[10px] text-slate-400">{inviteForm.personal_message.length}/500</p>
            </div>
          </div>
        </div>

        <div className="px-6 py-4 border-t border-slate-100 bg-slate-50 flex justify-end gap-2">
          <Button variant="outline" onClick={onClose} disabled={saving}
            data-testid="invite-dialog-cancel"
            className="h-9 text-xs">
            Cancel
          </Button>
          <Button onClick={onSubmit} disabled={!canSend}
            data-testid="invite-dialog-send"
            className="h-9 text-xs bg-blue-600 hover:bg-blue-700 text-white">
            {saving ? (<><RefreshCw className="w-3 h-3 mr-1.5 animate-spin" /> Sending…</>) : (<><Mail className="w-3 h-3 mr-1.5" /> Send Invite</>)}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}



// ─────────────────────────────────────────────────────────────────────────────
// Subscription Section — Agency platform fee subscription via Stripe Billing
// ─────────────────────────────────────────────────────────────────────────────
function SubscriptionSection() {
  const [status, setStatus] = useState(null);
  const [plans, setPlans] = useState(null);
  const [region, setRegion] = useState('AU');
  const [loading, setLoading] = useState(true);
  const [working, setWorking] = useState(false);
  const [confirmPlan, setConfirmPlan] = useState(null);
  const [savedMethods, setSavedMethods] = useState([]);

  const loadAll = useCallback(async () => {
    setLoading(true);
    try {
      const [statusRes, plansRes, pmRes] = await Promise.all([
        API.get('/billing/agency/status'),
        API.get('/billing/plans'),
        API.get('/billing/agency/payment-methods'),
      ]);
      setStatus(statusRes.data);
      setPlans(plansRes.data);
      setSavedMethods(pmRes.data.payment_methods || []);
    } catch (e) {
      toast.error('Failed to load subscription data');
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { loadAll(); }, [loadAll]);

  const subscribeDirect = async (planCode) => {
    setWorking(true);
    try {
      const res = await API.post('/billing/agency/subscribe', {
        region,
        plan_code: planCode,
      });
      if (res.data.subscription_id) {
        toast.success('Subscribed successfully!');
        setConfirmPlan(null);
        await loadAll();
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Subscription failed');
    } finally { setWorking(false); }
  };

  const openPortal = async () => {
    setWorking(true);
    try {
      const res = await API.post('/billing/agency/portal', {
        return_url: window.location.href,
      });
      window.location.href = res.data.portal_url;
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to open billing portal');
      setWorking(false);
    }
  };

  const cancelAtPeriodEnd = async () => {
    if (!window.confirm('Cancel your subscription at the end of the current period? You will keep access until then.')) return;
    setWorking(true);
    try {
      await API.post('/billing/agency/cancel', { at_period_end: true });
      toast.success('Subscription will cancel at period end');
      await loadAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to cancel');
    } finally { setWorking(false); }
  };

  if (loading) return (
    <div data-testid="subscription-section">
      <SectionHeader title="Subscription" description="Your Mylikeness platform subscription." />
      <Card><div className="h-32 flex items-center justify-center text-slate-400 text-sm">Loading…</div></Card>
    </div>
  );

  if (!status?.stripe_configured) {
    return (
      <div data-testid="subscription-section">
        <SectionHeader title="Subscription" description="Your Mylikeness platform subscription." />
        <Card>
          <div className="flex items-start gap-3 p-2">
            <AlertTriangle className="w-5 h-5 text-amber-500 flex-shrink-0 mt-0.5" />
            <div>
              <h3 className="text-sm font-semibold text-slate-800">Stripe is not configured</h3>
              <p className="text-xs text-slate-500 mt-1">Subscriptions are unavailable until the platform admin configures Stripe.</p>
            </div>
          </div>
        </Card>
      </div>
    );
  }

  const sub = status.subscription;
  const isSubscribed = !!status.subscribed;

  return (
    <div data-testid="subscription-section">
      <SectionHeader title="Subscription" description="Your Mylikeness platform subscription. Includes accrued creator-commission line items on your monthly invoice." />

      {/* Current subscription status */}
      {isSubscribed && sub ? (
        <Card>
          <div className="flex items-start justify-between gap-4 mb-4">
            <div>
              <div className="flex items-center gap-2 mb-1">
                <Badge data-testid="subscription-status-badge"
                  className={`text-[10px] uppercase tracking-wide ${
                    sub.status === 'active' ? 'bg-emerald-50 text-emerald-700 border-emerald-200' :
                    sub.status === 'past_due' ? 'bg-rose-50 text-rose-700 border-rose-200' :
                    sub.status === 'trialing' ? 'bg-blue-50 text-blue-700 border-blue-200' :
                    'bg-slate-100 text-slate-600 border-slate-200'
                  }`}>{sub.status}</Badge>
                {sub.cancel_at_period_end && (
                  <Badge className="text-[10px] uppercase bg-amber-50 text-amber-700 border-amber-200">Cancels at period end</Badge>
                )}
              </div>
              {sub.items?.[0] && (
                <h3 data-testid="subscription-plan-name" className="text-base font-semibold text-slate-900">
                  {sub.items[0].nickname || `${sub.items[0].currency} ${sub.items[0].amount.toFixed(2)} / ${sub.items[0].interval}`}
                </h3>
              )}
              <p className="text-xs text-slate-500 mt-1">
                Renews on {new Date((sub.current_period_end || 0) * 1000).toLocaleDateString()}
                {sub.payment_method?.last4 && (
                  <> · {sub.payment_method.type?.replace(/_/g, ' ')} ending in {sub.payment_method.last4}</>
                )}
              </p>
            </div>
            <Sparkles className="w-8 h-8 text-blue-400 flex-shrink-0" />
          </div>

          <div className="flex flex-wrap gap-2 pt-2 border-t border-slate-100">
            <Button data-testid="open-billing-portal-btn" size="sm" variant="outline"
              onClick={openPortal} disabled={working}
              className="h-8 text-xs">
              <ExternalLink className="w-3 h-3 mr-1.5" /> Manage in Stripe Portal
            </Button>
            {!sub.cancel_at_period_end && (
              <Button data-testid="cancel-subscription-btn" size="sm" variant="outline"
                onClick={cancelAtPeriodEnd} disabled={working}
                className="h-8 text-xs text-rose-600 hover:bg-rose-50 border-rose-100">
                Cancel subscription
              </Button>
            )}
          </div>
        </Card>
      ) : (
        // Plan picker (no subscription yet)
        <>
          <Card>
            <div className="flex items-start gap-3">
              <Zap className="w-5 h-5 text-blue-500 flex-shrink-0 mt-0.5" />
              <div className="flex-1">
                <h3 className="text-sm font-semibold text-slate-800">Choose your region & plan</h3>
                <p className="text-xs text-slate-500 mt-1">
                  Pricing is localised so you can pay via direct debit in your home currency
                  (BECS in Australia, ACH in the US, BACS in the UK, SEPA in the EU).
                  Your monthly invoice will also include any creator-commissions accrued during the period.
                </p>
              </div>
            </div>
          </Card>

          <div className="mb-4">
            <Label className="text-[11px] text-slate-600 font-medium block mb-2">Billing region</Label>
            <div className="flex flex-wrap gap-2" data-testid="region-selector">
              {(plans?.regions || []).map(r => (
                <button key={r.region}
                  data-testid={`region-${r.region}`}
                  onClick={() => setRegion(r.region)}
                  className={`px-3 py-2 rounded-lg text-xs font-medium border transition-all
                    ${region === r.region
                      ? 'bg-blue-50 border-blue-300 text-blue-700'
                      : 'bg-white border-slate-200 text-slate-600 hover:border-slate-300'}`}>
                  {r.region_label}
                  <span className="text-[10px] text-slate-400 ml-1">({r.currency})</span>
                </button>
              ))}
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {(plans?.regions?.find(r => r.region === region)?.plans || []).map(p => (
              <div key={p.code}
                data-testid={`plan-card-${p.code}`}
                className="bg-white rounded-xl border border-slate-200 p-5 hover:border-blue-300 hover:shadow-sm transition-all">
                <div className="flex items-baseline justify-between mb-2">
                  <h3 className="text-sm font-semibold text-slate-900">{p.label}</h3>
                  {p.code === 'teams' && <Badge className="text-[10px] bg-blue-50 text-blue-700 border-blue-200">Recommended</Badge>}
                </div>
                <div className="text-3xl font-bold text-slate-900 mb-3">
                  {p.currency} {p.amount.toFixed(0)}
                  <span className="text-xs font-normal text-slate-400 ml-1">/ month</span>
                </div>
                <ul className="text-xs text-slate-600 space-y-1.5 mb-5">
                  {p.code === 'solo_brand' ? (
                    <>
                      <li className="flex gap-2"><CheckCircle className="w-3.5 h-3.5 text-emerald-500 flex-shrink-0 mt-0.5" /> Single workspace owner</li>
                      <li className="flex gap-2"><CheckCircle className="w-3.5 h-3.5 text-emerald-500 flex-shrink-0 mt-0.5" /> Unlimited proposals & deals</li>
                      <li className="flex gap-2"><CheckCircle className="w-3.5 h-3.5 text-emerald-500 flex-shrink-0 mt-0.5" /> Direct creator payments</li>
                      <li className="flex gap-2"><CheckCircle className="w-3.5 h-3.5 text-emerald-500 flex-shrink-0 mt-0.5" /> Standard support</li>
                    </>
                  ) : (
                    <>
                      <li className="flex gap-2"><CheckCircle className="w-3.5 h-3.5 text-emerald-500 flex-shrink-0 mt-0.5" /> Multi-user team workspace</li>
                      <li className="flex gap-2"><CheckCircle className="w-3.5 h-3.5 text-emerald-500 flex-shrink-0 mt-0.5" /> Role-based permissions</li>
                      <li className="flex gap-2"><CheckCircle className="w-3.5 h-3.5 text-emerald-500 flex-shrink-0 mt-0.5" /> Payment approval workflows</li>
                      <li className="flex gap-2"><CheckCircle className="w-3.5 h-3.5 text-emerald-500 flex-shrink-0 mt-0.5" /> Priority support</li>
                    </>
                  )}
                </ul>
                <Button data-testid={`subscribe-${p.code}-btn`}
                  onClick={() => setConfirmPlan(p)} disabled={working || savedMethods.length === 0}
                  className="w-full h-9 text-xs bg-blue-600 hover:bg-blue-700 text-white">
                  {working ? <RefreshCw className="w-3 h-3 animate-spin" /> : <>Subscribe to {p.label}</>}
                </Button>
              </div>
            ))}
          </div>

          {savedMethods.length === 0 && (
            <div className="mt-3 flex items-center gap-3 p-3 bg-amber-50 rounded-lg border border-amber-200">
              <AlertTriangle className="w-4 h-4 text-amber-500 flex-shrink-0" />
              <p className="text-xs text-amber-700 flex-1">Add a payment method first to subscribe.</p>
              <PaymentSetupButton buttonLabel="Add method" className="h-8 text-xs" onSuccess={() => loadAll()} />
            </div>
          )}

          <p className="text-[10px] text-slate-400 mt-3">
            Your saved payment method will be charged monthly. Cancel anytime.
          </p>
        </>
      )}

      {/* Upcoming invoice */}
      {isSubscribed && status.upcoming_invoice && (
        <div className="mt-6">
          <h3 className="text-xs font-semibold text-slate-700 uppercase tracking-wide mb-2">Upcoming invoice</h3>
          <Card>
            <div className="flex items-baseline justify-between mb-3">
              <p className="text-xs text-slate-500">
                Next charge {status.upcoming_invoice.next_payment_attempt
                  ? `on ${new Date(status.upcoming_invoice.next_payment_attempt * 1000).toLocaleDateString()}`
                  : 'pending'}
              </p>
              <p data-testid="upcoming-invoice-total" className="text-lg font-bold text-slate-900">
                {status.upcoming_invoice.currency} {status.upcoming_invoice.amount_due.toFixed(2)}
              </p>
            </div>
            <div className="space-y-1.5">
              {(status.upcoming_invoice.lines || []).map((line, i) => (
                <div key={i} className="flex justify-between text-xs py-1.5 border-b border-slate-50 last:border-0">
                  <span className="text-slate-600">{line.description || 'Subscription line'}</span>
                  <span className="text-slate-800 font-medium">{line.currency} {line.amount.toFixed(2)}</span>
                </div>
              ))}
            </div>
          </Card>
        </div>
      )}

      {/* Recent invoices */}
      {isSubscribed && status.recent_invoices?.length > 0 && (
        <div className="mt-6">
          <h3 className="text-xs font-semibold text-slate-700 uppercase tracking-wide mb-2">Recent invoices</h3>
          <Card className="!p-0 overflow-hidden">
            <table className="w-full text-xs">
              <thead className="bg-slate-50 text-slate-500 uppercase text-[10px] tracking-wide">
                <tr>
                  <th className="text-left px-4 py-2.5 font-medium">Number</th>
                  <th className="text-left px-4 py-2.5 font-medium">Date</th>
                  <th className="text-left px-4 py-2.5 font-medium">Status</th>
                  <th className="text-right px-4 py-2.5 font-medium">Amount</th>
                  <th className="text-right px-4 py-2.5 font-medium"></th>
                </tr>
              </thead>
              <tbody>
                {status.recent_invoices.map(inv => (
                  <tr key={inv.id} data-testid={`invoice-row-${inv.id}`} className="border-t border-slate-100">
                    <td className="px-4 py-2.5 font-mono text-[11px] text-slate-600">{inv.number || inv.id.slice(0, 14)}</td>
                    <td className="px-4 py-2.5 text-slate-500">{new Date(inv.created * 1000).toLocaleDateString()}</td>
                    <td className="px-4 py-2.5">
                      <Badge className={`text-[10px] uppercase ${
                        inv.status === 'paid' ? 'bg-emerald-50 text-emerald-700 border-emerald-200' :
                        inv.status === 'open' ? 'bg-amber-50 text-amber-700 border-amber-200' :
                        'bg-slate-100 text-slate-600 border-slate-200'
                      }`}>{inv.status}</Badge>
                    </td>
                    <td className="px-4 py-2.5 text-right font-medium text-slate-800">{inv.currency} {(inv.amount_paid || inv.amount_due).toFixed(2)}</td>
                    <td className="px-4 py-2.5 text-right">
                      {inv.hosted_invoice_url && (
                        <a href={inv.hosted_invoice_url} target="_blank" rel="noopener noreferrer"
                          className="text-blue-600 hover:underline inline-flex items-center gap-1">
                          View <ExternalLink className="w-3 h-3" />
                        </a>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Card>
        </div>
      )}

      {/* Subscription confirmation dialog */}
      <Dialog open={!!confirmPlan} onOpenChange={(o) => { if (!o) setConfirmPlan(null); }}>
        <DialogContent className="max-w-sm" data-testid="subscribe-confirm-dialog">
          <DialogHeader>
            <DialogTitle className="text-base font-heading">Confirm Subscription</DialogTitle>
            <DialogDescription className="text-xs text-slate-500">You are about to subscribe to {confirmPlan?.label}.</DialogDescription>
          </DialogHeader>
          {confirmPlan && (
            <div className="space-y-4 mt-2">
              <div className="bg-slate-50 rounded-lg p-4 text-center">
                <p className="text-3xl font-bold text-slate-900">{confirmPlan.currency} {confirmPlan.amount?.toFixed(0)}</p>
                <p className="text-xs text-slate-500 mt-1">per month</p>
              </div>
              {savedMethods.length > 0 && (
                <div className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg">
                  <CreditCard className="w-4 h-4 text-slate-400" />
                  <span className="text-sm text-slate-700">{savedMethods.find(m => m.is_default)?.label || savedMethods[0]?.label || 'Saved payment method'}</span>
                </div>
              )}
              <p className="text-[11px] text-slate-500">Your payment method will be charged monthly. You can cancel anytime from this page.</p>
              <Button data-testid="confirm-subscribe-btn" onClick={() => subscribeDirect(confirmPlan.code)} disabled={working}
                className="w-full bg-blue-600 hover:bg-blue-700 text-white h-10">
                {working ? 'Processing...' : `Subscribe for ${confirmPlan.currency} ${confirmPlan.amount?.toFixed(0)}/mo`}
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
